import { format } from "date-fns";
import { FC, ReactNode } from "react";
import {
  AssetLabel,
  assetLabel,
  Decimal,
  IconDenom,
  useTranslation,
} from "rujira.ui";
import { LimitOrdersFragment$data } from "../Orders/__generated__/LimitOrdersFragment.graphql";
import { PairLink, useTradeUrl } from "../Orders/Common";
import {
  LabelSide,
  LabelType,
  Price,
  StatusFilled,
} from "../Orders/shared/labels";
import { OrderRow } from "./OrderRow";

export const LimitOrderRow: FC<{
  d: LimitOrdersFragment$data[number];
  actions: ReactNode;
  claimButton?: ReactNode;
}> = ({ d, actions, claimButton }) => {
  const { t } = useTranslation();
  const tradeTo = useTradeUrl(d.pair.assetBase, d.pair.assetQuote);
  const { assetOffer, assetAsk } =
    d.side === "base"
      ? { assetOffer: d.pair.assetBase, assetAsk: d.pair.assetQuote }
      : { assetOffer: d.pair.assetQuote, assetAsk: d.pair.assetBase };

  const decimal = (amount: bigint, symbol: string) => (
    <Decimal
      subscript
      round={4}
      amount={amount}
      symbol={symbol}
      symbolClassName="decimal__symbol--small color-grey condensed fw-500"
      clip
    />
  );

  const price = (
    <span className={d.side === "base" ? "color-red" : "color-teal"}>
      <Price {...d} />
    </span>
  );

  const label = (asset: typeof d.pair.assetBase) => (
    <AssetLabel
      asset={asset}
      Container={({ children }) => (
        <small className="color-grey fs-11">{children}</small>
      )}
    />
  );

  return (
    <OrderRow
      icons={
        <>
          <IconDenom denom={d.pair.assetBase.metadata.symbol} />
          <IconDenom denom={d.pair.assetQuote.metadata.symbol} />
        </>
      }
      title={
        <>
          {label(d.pair.assetBase)}
          <span className="color-grey">/</span>
          {label(d.pair.assetQuote)}
        </>
      }
      label={`${assetLabel(d.pair.assetBase)}/${assetLabel(d.pair.assetQuote)}`}
      meta={
        <span className="mono fs-12">
          {format(d.updatedAt, "dd/MM/yy")}{" "}
          <span className="color-grey">{format(d.updatedAt, "HH:mm")}</span>
        </span>
      }
      tags={
        <>
          <LabelSide {...d} />
          <LabelType {...d} />
        </>
      }
      primary={
        Number(d.remaining) > 0
          ? d.side === "base"
            ? decimal(BigInt(d.remaining), assetOffer.metadata.symbol)
            : decimal(BigInt(d.remainingValue), assetAsk.metadata.symbol)
          : undefined
      }
      secondary={price}
      headAction={claimButton}
      details={[
        {
          label: t("currentPrice"),
          value: (
            <Decimal
              subscript
              decimals={12}
              round={4}
              amount={BigInt(d.pair.book.center || 0)}
              symbol={d.pair.assetQuote.metadata.symbol}
              symbolClassName="decimal__symbol--small color-grey condensed fw-500"
              clip
            />
          ),
        },
        {
          label: t("orderPrice"),
          value: price,
        },
        {
          label: t("value"),
          value:
            d.side === "base"
              ? decimal(BigInt(d.offerValue), assetAsk.metadata.symbol)
              : decimal(BigInt(d.offer), assetOffer.metadata.symbol),
        },
        {
          label: t("amount"),
          value:
            d.side === "base"
              ? decimal(BigInt(d.offer), assetOffer.metadata.symbol)
              : decimal(BigInt(d.offerValue), assetAsk.metadata.symbol),
        },
        {
          label: t("remaining"),
          value:
            d.side === "base"
              ? decimal(BigInt(d.remaining), assetOffer.metadata.symbol)
              : decimal(BigInt(d.remainingValue), assetAsk.metadata.symbol),
        },
        {
          label: t("filled"),
          value: <StatusFilled {...d} plain />,
        },
        {
          label: t("unclaimed"),
          value: d.filled ? (
            decimal(BigInt(d.filled), assetAsk.metadata.symbol)
          ) : (
            <span className="color-grey">&mdash;</span>
          ),
        },
        {
          label: t("fee"),
          value: decimal(BigInt(d.filledFee), assetAsk.metadata.symbol),
        },
      ]}
      columns={3}
      actionsLastColumn
      actions={
        <>
          <PairLink to={tradeTo} label={t("viewPair")} />
          {actions}
        </>
      }
    />
  );
};
